import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import MoneyInput from '@v2/components/ui/MoneyInput';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import type { WalletAppliedItem } from './types';

type Mode = 'equal' | 'custom';
type Line = { key: number; month: string; amount: number | null };

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

function fmtMonth(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
}

function addMonths(ym: string, n: number) {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + n, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const cents = (v: number) => Math.round(v * 100);

/** Mesma divisão do servidor: partes iguais, centavos que sobram no último mês. */
function splitEqual(amount: number, months: string[]): Line[] {
    const total = cents(amount);
    const part = Math.floor(total / months.length);
    return months.map((month, i) => ({ key: i, month, amount: (i === months.length - 1 ? total - part * (months.length - 1) : part) / 100 }));
}

/**
 * Remaneja um estorno do mês para outros meses (super admin): em 1 mês, em vezes iguais ou
 * com valor livre por mês. O servidor recusa mês passado/fechado e valor acima do disponível.
 */
export default function WalletReallocateModal({
    sellerUserId,
    month,
    item,
    onClose,
}: {
    sellerUserId: number;
    month: string;
    item: WalletAppliedItem | null;
    onClose: () => void;
}) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const available = item?.amount ?? 0;
    const thisMonth = new Date().toISOString().slice(0, 7);
    // Daqui para frente, sem o mês do próprio estorno; fechados o servidor recusa.
    const months = useMemo(() => {
        const start = month > thisMonth ? month : thisMonth;
        return Array.from({ length: 25 }, (_, i) => addMonths(start, i)).filter((m) => m !== month).slice(0, 24);
    }, [month, thisMonth]);

    const [mode, setMode] = useState<Mode>('equal');
    const [toMove, setToMove] = useState<number | null>(available);
    const [start, setStart] = useState(months[0]);
    const [times, setTimes] = useState(1);
    const [lines, setLines] = useState<Line[]>([]);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (!item) return;
        setMode('equal');
        setToMove(item.amount);
        setStart(months[0]);
        setTimes(1);
        setLines([{ key: 0, month: months[0], amount: item.amount }]);
        setReason('');
    }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const plan: Line[] = useMemo(() => {
        if (mode === 'custom') return lines;
        if (!toMove || toMove <= 0) return [];
        return splitEqual(toMove, Array.from({ length: times }, (_, i) => addMonths(start, i)));
    }, [mode, lines, toMove, start, times]);

    const total = plan.reduce((acc, l) => acc + cents(l.amount ?? 0), 0) / 100;
    const remaining = (cents(available) - cents(total)) / 100;
    const over = remaining < 0;
    const duplicate = new Set(plan.map((l) => l.month)).size !== plan.length;
    const invalidMonth = plan.some((l) => l.month === month || l.month < thisMonth);
    const valid = plan.length > 0 && total > 0 && !over && !duplicate && !invalidMonth
        && plan.every((l) => (l.amount ?? 0) > 0) && reason.trim().length >= 3;

    const save = useMutation({
        mutationFn: () => api.post(`/commissioning/sellers/${sellerUserId}/wallet/entries/${item!.id}/reallocate`, {
            reason: reason.trim(),
            targets: plan.map((l) => ({ competence_month: l.month, amount: l.amount })),
        }),
        onSuccess: (res) => {
            toast.success(res.data?.message ?? t('v2.commissioning.wallet.reallocated'));
            void qc.invalidateQueries({ queryKey: ['commissioning', 'seller-month-history'] });
            void qc.invalidateQueries({ queryKey: ['commissioning'] });
            onClose();
        },
        onError: (err) => {
            const res = (err as { response?: { data?: { message?: string } } })?.response?.data;
            toast.error(res?.message ?? t('v2.commissioning.wallet.reallocate_not_allowed'));
        },
    });

    const selectCls = 'h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-zinc-700 dark:bg-zinc-900';
    const moneyCls = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-right text-sm tabular-nums dark:border-zinc-700 dark:bg-zinc-900';

    return (
        <V2Modal
            open={item !== null}
            onClose={onClose}
            title={t('v2.commissioning.wallet.reallocate_title')}
            description={item ? `${item.deal_number ? `#${item.deal_number} · ` : ''}${item.deal_title ?? item.note ?? ''}` : undefined}
            size="lg"
            footer={(
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        {t('v2.commissioning.director_approval.close')}
                    </Button>
                    <Button type="button" isLoading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>
                        {t('v2.commissioning.wallet.reallocate_confirm')}
                    </Button>
                </div>
            )}
        >
            {item && (
                <div className="space-y-4 text-sm">
                    <div className="flex items-center justify-between rounded-xl bg-rose-50 px-3 py-2 dark:bg-rose-950/30">
                        <span className="text-xs font-semibold text-rose-800 dark:text-rose-200">
                            {t('v2.commissioning.wallet.reallocate_available')} · <span className="capitalize">{fmtMonth(month)}</span>
                        </span>
                        <span className="font-black tabular-nums text-rose-700 dark:text-rose-300">{brl(available)}</span>
                    </div>

                    <div className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold dark:bg-zinc-800">
                        {(['equal', 'custom'] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => {
                                    if (m === 'custom' && mode === 'equal') setLines(plan.map((l, i) => ({ ...l, key: i })));
                                    setMode(m);
                                }}
                                className={cn(
                                    'rounded-md px-3 py-1.5 transition',
                                    mode === m ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100' : 'text-gray-500 hover:text-gray-800 dark:text-zinc-400',
                                )}
                            >
                                {t(`v2.commissioning.wallet.reallocate_mode_${m}`)}
                            </button>
                        ))}
                    </div>

                    {mode === 'equal' ? (
                        <div className="grid gap-3 sm:grid-cols-3">
                            <label className="block text-xs font-semibold text-gray-600 dark:text-zinc-300">
                                {t('v2.commissioning.wallet.reallocate_amount_to_move')}
                                <MoneyInput value={toMove} onChange={setToMove} className={cn(moneyCls, 'mt-1')} />
                            </label>
                            <label className="block text-xs font-semibold text-gray-600 dark:text-zinc-300">
                                {t('v2.commissioning.wallet.reallocate_start')}
                                <select value={start} onChange={(e) => setStart(e.target.value)} className={cn(selectCls, 'mt-1 w-full capitalize')}>
                                    {months.map((m) => (
                                        <option key={m} value={m}>{fmtMonth(m)}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block text-xs font-semibold text-gray-600 dark:text-zinc-300">
                                {t('v2.commissioning.wallet.reallocate_times')}
                                <select value={times} onChange={(e) => setTimes(Number(e.target.value))} className={cn(selectCls, 'mt-1 w-full')}>
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                                        <option key={n} value={n}>{n}x</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {lines.map((line) => (
                                <div key={line.key} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                                    <select
                                        value={line.month}
                                        onChange={(e) => setLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, month: e.target.value } : l)))}
                                        className={cn(selectCls, 'capitalize')}
                                        aria-label={t('v2.commissioning.wallet.reallocate_month')}
                                    >
                                        {months.map((m) => (
                                            <option key={m} value={m}>{fmtMonth(m)}</option>
                                        ))}
                                    </select>
                                    <MoneyInput
                                        value={line.amount}
                                        onChange={(v) => setLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, amount: v } : l)))}
                                        className={moneyCls}
                                        aria-label={t('v2.commissioning.wallet.reallocate_value')}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                                        disabled={lines.length === 1}
                                        className="rounded-lg p-2 text-gray-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30"
                                        aria-label="Remover"
                                    >
                                        <PiTrashBold className="size-4" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    const used = new Set(lines.map((l) => l.month));
                                    const next = months.find((m) => !used.has(m)) ?? months[0];
                                    setLines((prev) => [...prev, { key: Date.now(), month: next, amount: remaining > 0 ? remaining : null }]);
                                }}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                                <PiPlusBold className="size-3.5" /> {t('v2.commissioning.wallet.reallocate_add_month')}
                            </button>
                        </div>
                    )}

                    {/* Como fica: mês de origem + cada mês que recebe */}
                    <div className="rounded-xl border border-gray-200 p-3 dark:border-zinc-700">
                        <ul className="space-y-1 text-xs">
                            <li className="flex justify-between text-gray-500">
                                <span>{t('v2.commissioning.wallet.reallocate_stays', { month: fmtMonth(month) })}</span>
                                <span className={cn('tabular-nums', over && 'font-bold text-rose-600')}>− {brl(Math.max(remaining, 0))}</span>
                            </li>
                            {plan.map((l) => (
                                <li key={l.key} className="flex justify-between">
                                    <span className="capitalize">{fmtMonth(l.month)}</span>
                                    <span className="tabular-nums font-semibold text-rose-700 dark:text-rose-300">− {brl(l.amount ?? 0)}</span>
                                </li>
                            ))}
                            <li className="flex justify-between border-t border-gray-100 pt-1 font-bold dark:border-zinc-800">
                                <span>{t('v2.commissioning.wallet.reallocate_total')}</span>
                                <span className="tabular-nums">{brl(total)}</span>
                            </li>
                        </ul>
                        {over && <p className="mt-2 text-xs font-semibold text-rose-600">{t('v2.commissioning.wallet.reallocate_over', { amount: brl(-remaining) })}</p>}
                        {duplicate && <p className="mt-2 text-xs font-semibold text-rose-600">{t('v2.commissioning.wallet.reallocate_duplicate_month')}</p>}
                    </div>

                    <label className="block text-xs font-semibold text-gray-600 dark:text-zinc-300">
                        {t('v2.commissioning.wallet.reallocate_reason')}
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                </div>
            )}
        </V2Modal>
    );
}
